import React, { useState, useMemo } from 'react';
import { Alert, Typography, Link } from '@one-thd/sui-atomic-components';
import { routeBuilder } from '../../../utils/general';
import { IOrderDetailsResponse } from '../../../models/order-details';
import { isBopisOutOfStockRecoverable } from '../../order-details/util/FulfillmentStatus';

export interface BopisOutOfStockRecoveryAlertProps {
  allOrderDetails: IOrderDetailsResponse[] | [];
}

export const BopisOutOfStockRecoveryAlert = ({
  allOrderDetails,
}: BopisOutOfStockRecoveryAlertProps) => {
  const [showAlert, setShowAlert] = useState(true);

  /* This function filters unique BOPIS orders where at least one fulfillmentGroup has a cancellationStatus of 'OUT_OF_STOCK', 
  isOosRecoveryEnabled set to true in actions, and a non-empty oosRecoveryOptions array. */
  const bopisOutOfStockRecoverableOrders = useMemo(() => {
    const uniqueOrderNumbers = new Set();
    return allOrderDetails?.filter((order) => {
      const orderNumber = order?.orderNumber || order?.orderNumbers?.[0];
      if (!orderNumber || uniqueOrderNumbers.has(orderNumber)) {
        return false;
      }
      const hasBopisRecoverable = order?.fulfillmentGroups?.some(
        (fulfillmentGroup) =>
          fulfillmentGroup?.fulfillmentType === 'BuyOnlinePickupInStore' &&
          isBopisOutOfStockRecoverable(fulfillmentGroup),
      );
      if (hasBopisRecoverable) {
        uniqueOrderNumbers.add(orderNumber);
        return true;
      }
      return false;
    });
  }, [allOrderDetails]);

  const renderOrderLinks = (
    outOfStockRecoverableOrders: IOrderDetailsResponse[],
  ) => {
    return outOfStockRecoverableOrders.map(
      (orderDetailsData, index: number) => {
        const orderNumber =
          orderDetailsData?.orderNumber || orderDetailsData?.orderNumbers?.[0];
        const orderDetailsUrl = `/myaccount/order-details${routeBuilder({
          orderNumber,
          salesDate: orderDetailsData?.salesDate,
          storeNumber: orderDetailsData?.storeNumber,
          transactionId: orderDetailsData?.transactionId,
          orderOrigin: orderDetailsData?.orderOrigin,
          registerNumber: orderDetailsData?.registerNumber,
          nickName: orderDetailsData?.nickName,
        })}`;
        return (
          <span key={`out-of-stock-recoverable-order-${index}`}>
            <Link
              href={orderDetailsUrl}
              variant="body-sm"
            >{`Order #${orderNumber}`}</Link>
            {index < outOfStockRecoverableOrders.length - 1 && ', '}
          </span>
        );
      },
    );
  };

  if (!bopisOutOfStockRecoverableOrders?.length || !showAlert) return null;

  return (
    <Alert
      status="warning"
      size="large"
      headline="Some of your items are out of stock."
      onClose={() => {
        setShowAlert(false);
      }}
    >
      <Typography variant="body-sm">
        Review your options for{' '}
        {renderOrderLinks(bopisOutOfStockRecoverableOrders)}
      </Typography>
    </Alert>
  );
};
